<template>
  <div class="knowledgebase">
    <div class="meta-nav">
        <ul>
            <li><a href="#people">People</a></li>
            <li><a href="#funnels">Funnels</a></li>
            <li><a href="#messages">Messages</a></li>
            <li><a href="#billing">Billing</a></li>
            <li><a href="#developers">Developers</a></li>
        </ul>
    </div>
    <h3 id="overview">A Quick Demo</h3>
    <video controls poster="https://www.drip-funnels.com/wp-content/uploads/2020/07/screenshot-1.png" src="https://www.drip-funnels.com/wp-content/uploads/2020/07/intro.mp4"></video>
    <h3 id="people">People</h3>
    <ul class="questions">
        <li>
            <h4>How can I add people?</h4>
            <p>To add one or more people to Drip Funnels, visit the <router-link to="/">People</router-link> tab. Then, find the "Add People" button. Follow the instructions in the pop-up box to complete the process.</p>
        </li>
        <li>
            <h4>Can I use my WordPress users in my funnels?</h4>
            <p>This is something we are looking into. =)</p>
        </li>
        <li>
            <h4>How do I add or remove attributes for people?</h4>
            <p>Visit the <router-link to="/settings/attributes">Attributes</router-link> tab (found under <router-link to="/settings/attributes">Settings</router-link>). There, you can click on "+ Add Attribute" to add to the list!</p>
        </li>
        <li>
            <h4>How do I update the information for a person?</h4>
            <p>When you're on the <router-link to="/">People</router-link> tab, you can click into any table cell to update the information for that person.</p>
        </li>
        <li>
            <h4>How can I delete a person from my list?</h4>
            <p>To delete a person, find them on the <router-link to="/">People</router-link> tab. The column on the <em>far right</em> has a trash icon that can be used to remove that person permanently from your list. Because this is a permanent deletion, their "Unsubscribed" status will not be persisted.</p>
        </li>
    </ul>
    <h3 id="funnels">Funnels</h3>
    <ul class="questions">
        <li>
            <h4>How do I create a Funnel campaign?</h4>
            <p>To create a campaign, visit the <router-link to="/funnels">Funnels</router-link> tab and click "+ Add Funnel". Enter a name, click "Next", and then enter the details of your funnel.</p>
        </li>
        <li>
            <h4>How do I add people to a Funnel?</h4>
            <p>Go to the <router-link to="/">People</router-link> tab. Then, check the box beside each person you want to initiate. On the right, above the table, you should see an "Actions" dropdown. Select "Add to Funnel".</p>
        </li>
        <li>
            <h4>How can I see and remove the emails that are queued to be sent?</h4>
            <p>To see the messages in queue, visit the <router-link to="/settings/queue">Pending Events</router-link> tab (on the <router-link to="/settings/attributes">Settings</router-link> page).</p>
        </li>
        <li>
            <h4>How can I unsubscribe a person OR check if they are unsubscribed?</h4>
            <p>First, visit the <router-link to="/settings/attributes">attributes</router-link> tab and add a "checkbox" column named "Unsubscribe". Now, when you visit the <router-link to="/">People</router-link> tab, you'll see an "Unsubscribe" column, with a checkbox for each person. Once unsubscribed, they will no longer receive any messages.</p>
        </li>
        <li>
            <h4>How can I learn more about drip email funnels?</h4>
            <p>We have a 7-step course for you to become an email-funnel master! <a href="https://www.drip-funnels.com/secrets/">Sign up here!</a></p>
        </li>

    </ul>
    <h3 id="messages">Messages</h3>
    <ul class="questions">
        <li>
            <h4>How can I send one message to one person?</h4>
            <p>You could create a new Funnel with just 1 message that sends immediately.</p>
        </li>
        <li>
            <h4>How do I use a person's attributes in a message?</h4>
            <p>In messages, you can use curly braces to represent the person's information. The fields map exactly to your <router-link to="/settings/attributes">attributes</router-link> page. <span v-pre>For example, "{{Name}}". If you'd like to supply a default fallback, you can use a pipe, "Hey, {{Name|there}}!"</span></p>
        </li>
    </ul>
    <h3 id="billing">Billing</h3>
    <ul class="questions">
        <li>
            <h4>What are the limits of the free version?</h4>
            <p>The free version has a people-limit of 100 and a funnel-limit of 1. If you already have an activation code, click <a href="javascript:;" @click="app.showActivationModal">here</a>. To purchase an activation code, click <a href="https://www.drip-funnels.com/pricing/" target="_blank">here.</a> </p>
        </li>
        <li>
            <h4>Can I use my subscription on multiple websites?</h4>
            <p>Yes!</p>
        </li>
        <li>
            <h4>How can I re-enter my activation key?</h4>
            <p><a href="javascript:;" @click="app.showActivationModal()">Click here</a> to re-enter your activation key.</p>
        </li>
        <li>
            <h4>Have a question about billing?</h4>
            <p>Send us an email at <a href="mailto:hello@drip-funnels.com">hello@drip-funnels.com</a>.</p>
        </li>
    </ul>
    <h3 id="developers">Developers</h3>
    <ul class="questions">
        <li v-if="snippet">
            <h4>Can I load Drip Funnels on a different site?</h4>
            <p>To load your Drip Funnels on <em>any</em> domain, include the snippet below before your closing <strong>&lt;/body&gt;</strong> tag.</p>
            <pre class="pre monospace">{{snippet}}</pre>
        </li>
        <li>
            <h4>How can I add a person <em>programatically</em>?</h4>
            <p>Drip Funnels exposes a simple library on the front end of your site. Field names correspond with your <router-link to="/settings/attributes">Attributes</router-link> tab:</p>
            <pre class="monospace">window.marketing('identify', { Name: 'Bob', Email: 'bob@drip-funnels.com' });</pre>
        </li>
        <li>
            <h4>Do I need to wait for a response?</h4>
            <p>Events are queued and processed asynchronously in the order that they are received. There is no need to wait for a response after calling `identify`.</p>
        </li>
        <li>
            <h4>How can I add a person to a funnel?</h4>
            <p>First, you must ensure that you have used the `identify` function above. After you find the funnel ID in the dashboard (1234 in this case), its easy to start them on a funnel:</p>
            <pre class="monospace">window.marketing('join', 1234);</pre>
        </li>
        <!-- <li>
            <h4>How can I remove a person from a funnel?</h4>
            <p>First, you must ensure that you have used the `identify` function above. After you find the funnel ID in the dashboard (1234 in this case), its easy to remove them from a funnel:</p>
            <pre class="monospace">window.marketing('remove', 1234);</pre>
        </li>
        <li>
            <h4>How can I remove a person from all ongoing funnels?</h4>
            <p>First, you must ensure that you have used the `identify` function above.</p>
            <pre class="monospace">window.marketing('removeAll');</pre>
        </li> -->
    </ul>
  </div>
</template>

<script>
export default {
  name: 'knowledgebase',
  props: ['app'],
  computed: {
    snippet() {
        return (window.df.snippet || '').trim();
    },
  }
};
</script>
